import React, { useMemo, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import { AddonIdDiscover } from '@jira/navigation-apps__constants/src/AddonIdDiscover.tsx';
import { MenuListItem } from '@atlaskit/side-nav-items/menu-list-item';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { MenuSection, MenuSectionHeading } from '@atlaskit/side-nav-items/menu-section';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import { ForgeScreenEvent } from '@jira/forge__forge-ui-analytics/src/common/ui/index.tsx';

import { TriggerAnalyticsWrapper } from '@jira/forge__forge-ui-analytics/src/ui/global-page/index.tsx';
import { GLOBAL_PAGE_MODULE } from '@jira/forge__forge-ui-constants/src/constants.tsx';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import {
	AsyncDiscoverSection,
	AsyncDiscoverSectionPostOffice,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-content-marketplace-recommendations/src/ui/index.tsx';
import { NestedItemDropTarget } from '@atlassian/navigation-drag-and-drop/nested-item-drop-target';
import { useReorderableStarredItems } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/utils/use-reorderable-starred-items.tsx';
import { Nav4ConnectMenuItem } from '../connect-menu-item/Nav4ConnectMenuItem.tsx';
import { Nav4ForgeMenuItem } from '../forge-menu-item/Nav4ForgeMenuItem.tsx';
import { useAppReordering } from '../../utils/useAppReordering.tsx';
import type { Nav4AppsContent$key } from './__generated__/Nav4AppsContent.graphql';
import type { Nav4AppsContent_navigationCustomisation$key } from './__generated__/Nav4AppsContent_navigationCustomisation.graphql';

const yourApps = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-apps.common.content.your-apps',

	defaultMessage: 'Your apps',
	description: 'Message for installed global apps heading in the Apps menu items',
});

type Nav4AppsContentProps = {
	queryRef: Nav4AppsContent$key;
	navigationCustomisationFragment?: Nav4AppsContent_navigationCustomisation$key | null;
};

export function Nav4AppsContent({
	queryRef,
	navigationCustomisationFragment,
}: Nav4AppsContentProps): React.JSX.Element | null {
	// eslint-disable-next-line @atlassian/relay/use-fragment-naming, @atlassian/relay/use-fragment-naming
	const data = useFragment<Nav4AppsContent$key>(
		graphql`
			fragment Nav4AppsContent on JiraQuery {
				globalAppNavigationItems(cloudId: $cloudId) {
					pageInfo {
						hasNextPage
					}
					edges @required(action: THROW) {
						node @required(action: THROW) {
							id @required(action: THROW)
							... on JiraAppNavigationItem {
								label
								appId
								appType
							}
							...Nav4ConnectMenuItem @dangerously_unaliased_fixme
							...Nav4ForgeMenuItem @dangerously_unaliased_fixme
						}
					}
				}
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		queryRef,
	);

	const navigationCustomisation = useFragment(
		graphql`
			fragment Nav4AppsContent_navigationCustomisation on SettingsNavigationCustomisation {
				sidebar {
					nodes {
						# It is necessary to suppress the lint rule, because these ARE used by useReorderableStarredItems
						# eslint-disable-next-line @atlassian/relay/unused-fields
						menuId
						# eslint-disable-next-line @atlassian/relay/unused-fields
						visible
					}
				}
			}
		`,
		navigationCustomisationFragment,
	);

	const { formatMessage } = useIntl();

	let apps: {
		currentItems: Array<{ id: string; label: string }>;
		currentOrder: Array<{ menuId: string; visible: boolean }>;
		list: React.JSX.Element[];
	} = {
		currentItems: [],
		currentOrder: [],
		list: [],
	};
	if (expVal('navx-2365-add_dnd_to_jira_l2_menu_items', 'isEnabled', false)) {
		type AppEdge = NonNullable<
			NonNullable<NonNullable<typeof data.globalAppNavigationItems>['edges']>[number]
		>;
		type AppNode = NonNullable<AppEdge['node']>;

		// eslint-disable-next-line react-hooks/rules-of-hooks
		const appNodes: AppNode[] = useMemo(
			() =>
				(data.globalAppNavigationItems?.edges
					?.filter((edge): edge is AppEdge => edge != null && edge !== undefined)
					?.map((edge: AppEdge) => edge.node)
					.filter((node): node is AppNode => node != null || node !== undefined)
					.filter((node) => {
						// Keep only Forge and Connect apps on the list
						if (node?.appType !== 'CONNECT' && node?.appType !== 'FORGE') return false;
						// Filter out system apps just like it was done in the old navigation
						return node?.appId !== AddonIdDiscover;
					})
					.filter((node) => node)
					.sort((a, b) => (a?.label || '').localeCompare(b?.label || '')) ||
					[]) ??
				[],
			[data.globalAppNavigationItems?.edges],
		);

		// eslint-disable-next-line react-hooks/rules-of-hooks
		apps = useReorderableStarredItems<AppNode>({
			favourites: appNodes,
			lastOrder: navigationCustomisation?.sidebar?.nodes,
			getId: (item) => item.appId || '',
			getLabel: (item) => item?.label?.toString() || '',
			getHasNextPage: () => Boolean(data.globalAppNavigationItems?.pageInfo?.hasNextPage),
			renderMenuItem: (node, currentItems, currentOrder, item) => {
				switch (node?.appType) {
					case 'CONNECT':
						return (
							<Nav4ConnectMenuItem
								key={node.id}
								queryRef={node}
								currentItems={currentItems}
								currentOrder={currentOrder}
								item={item}
							/>
						);
					case 'FORGE':
						return (
							<Nav4ForgeMenuItem
								key={node.id}
								queryRef={node}
								currentItems={currentItems}
								currentOrder={currentOrder}
								item={item}
							/>
						);
					default:
						return <></>;
				}
			},
			maxItems: 1000000,
		});
	}

	const menuItemNodes = useMemo(
		() =>
			(data?.globalAppNavigationItems?.edges || [])
				.filter((edge) => {
					// Keep only Forge and Connect apps on the list
					if (edge?.node?.appType !== 'CONNECT' && edge?.node?.appType !== 'FORGE') return false;
					// Filter out system apps just like it was done in the old navigation
					return edge?.node?.appId !== AddonIdDiscover;
				})
				.map((edge) => edge?.node)
				.sort((a, b) => (a?.label || '').localeCompare(b?.label || '')) || [],
		[data?.globalAppNavigationItems],
	);

	const renderedMenuItems = useMemo(
		() =>
			menuItemNodes
				.map((node) => {
					switch (node?.appType) {
						case 'CONNECT':
							return (
								<Nav4ConnectMenuItem
									key={node.id}
									queryRef={node}
									currentItems={undefined}
									currentOrder={undefined}
									item={undefined}
								/>
							);
						case 'FORGE':
							return (
								<Nav4ForgeMenuItem
									key={node.id}
									queryRef={node}
									currentItems={undefined}
									currentOrder={undefined}
									item={undefined}
								/>
							);
						default:
							return null;
					}
				})
				.filter(Boolean),
		[menuItemNodes],
	);

	const hasForgeApps = useMemo(
		() => menuItemNodes.some((node) => node?.appType === 'FORGE'),
		[menuItemNodes],
	);

	const renderDiscoverAppsSection = () => {
		if (!fg('jira_nav4_apps_menu_recommend')) return null;

		if (expVal('enable_jira_sidenav_2p_app_recommendations', 'isEnabled', false)) {
			return (
				<MenuListItem>
					<AsyncDiscoverSectionPostOffice />
				</MenuListItem>
			);
		}
		return (
			<MenuListItem>
				<AsyncDiscoverSection />
			</MenuListItem>
		);
	};

	if (expVal('navx-2365-add_dnd_to_jira_l2_menu_items', 'isEnabled', false)) {
		// On experiment cleanup it should to remove this lint suppression and move the hook to a safe place...
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { entityAri, ownerAri, group } = useAppReordering();
		return apps.list.length ? (
			<ForgeAnalyticsWrapper shouldReport={hasForgeApps}>
				<MenuListItem>
					<MenuSection>
						<MenuSectionHeading>{formatMessage(yourApps)}</MenuSectionHeading>
						<NestedItemDropTarget
							currentItems={apps.currentItems}
							currentOrder={apps.currentOrder}
							entityAri={entityAri}
							group={group}
							ownerAri={ownerAri}
						>
							<MenuList>
								{apps.list}
								{hasForgeApps && (
									<ForgeScreenEvent attributes={{ moduleType: GLOBAL_PAGE_MODULE }} />
								)}
							</MenuList>
						</NestedItemDropTarget>
					</MenuSection>
				</MenuListItem>
				{renderDiscoverAppsSection()}
			</ForgeAnalyticsWrapper>
		) : null;
	}

	return renderedMenuItems.length ? (
		<ForgeAnalyticsWrapper shouldReport={hasForgeApps}>
			<MenuListItem>
				<MenuSection>
					<MenuSectionHeading>{formatMessage(yourApps)}</MenuSectionHeading>
					<MenuList>
						{renderedMenuItems}
						{hasForgeApps && <ForgeScreenEvent attributes={{ moduleType: GLOBAL_PAGE_MODULE }} />}
					</MenuList>
				</MenuSection>
			</MenuListItem>
			{renderDiscoverAppsSection()}
		</ForgeAnalyticsWrapper>
	) : null;
}

function ForgeAnalyticsWrapper({
	shouldReport,
	children,
}: {
	shouldReport: boolean;
	children: ReactNode;
}) {
	return shouldReport ? <TriggerAnalyticsWrapper>{children}</TriggerAnalyticsWrapper> : children;
}
